import { defineStore } from "pinia";
import * as adminApi from "@/http/adminApi.js";
import { reactive, ref } from "vue";
import Swal from "sweetalert2";
import { useRouter } from "vue-router";
import router from "@/router/routes";
import moment from "moment-jalaali";

// Utility functions
const handleError = (message) => {
    Swal.fire({
        title: message,
        timer: 2000,
        showConfirmButton: false,
        timerProgressBar: true,
        position: "top",
        icon: "error",
        toast: true,
        width: "30rem",
    });
};

const handleSuccess = (message) => {
    Swal.fire({
        title: message,
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        position: "top",
        icon: "success",
        toast: true,
        width: "30rem",
    });
};
const handleWarning = (title, message) => {
    Swal.fire({
        title: title,
        text: message,
        icon: "question",
    });
};

// Dashboard data store
export const useDashboardData = defineStore("allDataCount", () => {
    const dashboardData = ref([]);
    const loadingDashboardData = ref(false);

    const allDashboardData = async () => {
        loadingDashboardData.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.allDataCount();
            dashboardData.value = data;
        } catch (err) {
            handleError("در دریافت اطلاعات مشکلی پیش آمده است!");
            console.log(err);
        } finally {
            loadingDashboardData.value = false;
        }
    };
    const disableMenus = async () => {
        handleWarning(
            "این قابلیت قفل است",
            "برای خریداری این قابلیت با پشتیبانی تماس بگیرید"
        );
    };
    return {
        allDashboardData,
        dashboardData,
        loadingDashboardData,
        disableMenus,
    };
});

// Trucks store
export const useTrucksStore = defineStore("trucksStore", () => {
    const trucks = ref([]);
    const loading = ref(false);
    const changeStatusLoading = ref(false);

    const fetchAllTrucks = async () => {
        loading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.allTrucks();
            trucks.value = data;
        } catch (err) {
            handleError("در دریافت اطلاعات نفتکش ها مشکلی پیش آمده است!");
        } finally {
            loading.value = false;
        }
    };

    const changeCarActivation = async (carID, isActive) => {
        changeStatusLoading.value = true;
        try {
            await adminApi.csrfCookies();
            await adminApi.changeCarActivationApi(carID, isActive ? 0 : 1);
            const truck = trucks.value.find((truck) => truck.id === carID);
            if (truck) {
                truck.is_active = isActive ? 0 : 1;
            }
        } catch (err) {
            handleError("در تغییر وضعیت کاربر مشکلی پیش آمده است");
        } finally {
            changeStatusLoading.value = false;
        }
    };

    return {
        trucks,
        fetchAllTrucks,
        loading,
        changeCarActivation,
        changeStatusLoading,
    };
});

// Update car store
export const useUpdateCarStore = defineStore("updateCarStore", () => {
    const router = useRouter();
    const truckInfo = ref([]);
    const loadingUpdateCars = ref(false);
    const tagValidationErr = ref(0);
    const updateCarData = reactive({
        smartcode: "",
        smartcodeErr: "",
        tag: "",
        tagErr: "",
        tagSerial: "",
        tagSerialErr: "",
        tag_serial_num: "",
        tag_serial_numErr: "",
        tankertype: "",
        tankertypeErr: "",
        factory: "",
        factoryErr: "",
        chassiscode: "",
        chassiscodeErr: "",
        enginecode: "",
        enginecodeErr: "",
        vincode: "",
        vincodeErr: "",
        year: "",
        yearErr: "",
    });

    const getUpdateCarData = async (truckId) => {
        loadingUpdateCars.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.editCarsApi(truckId);
            truckInfo.value = data;
            Object.assign(updateCarData, data);
            updateCarData.vincode = updateCarData.vincode?.toUpperCase();
        } catch (err) {
            handleError("در بارگزاری اطلاعات مشکلی پیش آمده است.");
        } finally {
            loadingUpdateCars.value = false;
        }
    };
    const tagValidate = async () => {
        const tagVerify = new RegExp("\\b(\\d{3})([عﻉ])(\\d{2})\\b");

        if (!updateCarData.tag) {
            tagValidationErr.value = 1;
            updateCarData.tagErr = "پلاک را وارد کنید";
            return;
        }
        if (!tagVerify.test(updateCarData.tag)) {
            tagValidationErr.value = 1;
            updateCarData.tagErr = "پلاک وارد شده معتبر نیست";
            return;
        }
        tagValidationErr.value = 0;
        updateCarData.tagErr = "";
        // try {
        //   const { data } = await adminApi.verifyPlateApi(updateCarData.tag);

        //   if (data === 1 && updateCarData.tag != truckInfo.value.tag) {
        //     updateCarData.tagErr = "این پلاک قبلا ثبت شده است";
        //     tagValidationErr.value = 1;
        //   } else {
        //     tagValidationErr.value = 0;
        //     updateCarData.tagErr = "";
        //   }
        // } catch (error) {
        //   handleError("خطا در بررسی شماره پلاک");
        // }
    };
    const editCarData = async (carID) => {
        const result = await Swal.fire({
            text: "از ویرایش نفتکش اطمینان دارید؟",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "ویرایش",
            cancelButtonText: "انصراف",
        });

        if (result.isConfirmed) {
            loadingUpdateCars.value = true;
            try {
                await adminApi.csrfCookies();
                const { data } = await adminApi.updateCarsApi(
                    carID,
                    updateCarData.smartcode,
                    updateCarData.tag,
                    updateCarData.tagSerial,
                    updateCarData.tag_serial_num,
                    updateCarData.tankertype,
                    updateCarData.factory,
                    updateCarData.chassiscode,
                    updateCarData.enginecode,
                    updateCarData.vincode,
                    updateCarData.year
                );

                console.log(data);

                handleSuccess("ویرایش با موفقیت انجام شد");
                router.push({ path: "/admin/trucks" });
            } catch (err) {
                handleError("در ویرایش نفتکش مشکلی پیش آمده است");
                console.log(err);
            } finally {
                loadingUpdateCars.value = false;
            }
        }
    };

    const validateCarData = (carID) => {
        let isValid = true;
        tagValidate();

        if (!updateCarData.tag_serial_num) {
            updateCarData.tag_serial_numErr = "شماره سریال پلاک را وارد کنید";
            isValid = false;
        } else {
            updateCarData.tag_serial_numErr = "";
        }

        if (isValid == true && !tagValidationErr.value) {
            editCarData(carID);
        }
    };

    return {
        getUpdateCarData,
        updateCarData,
        loadingUpdateCars,
        editCarData,
        validateCarData,
        tagValidate,
        tagValidationErr,
    };
});

// User store
export const useUserStore = defineStore("userStore", () => {
    const users = ref([]);
    const router = useRouter();
    const nationalcodeResponse = ref("");
    const nationalcodeValidationErr = ref(0);
    const tagValidationErr = ref(0);
    const phoneCheckResponse = ref("");
    const usersLoading = ref(false);
    const changeStatusLoading = ref(false);

    const registerFormData = reactive({
        name: "",
        nameErr: "",
        family: "",
        familyErr: "",
        phone: "",
        phoneErr: "",
        nationalcode: "",
        nationalcodeErr: "",
        smartcode: "",
        tag: "",
        tagErr: "",
        tag_serial_num: "",
        tag_serial_numErr: "",
        tankertype: "",
        factory: "",
        chassiscode: "",
        enginecode: "",
        vincode: "",
        year: "",
    });
    const phoneVerificationError = ref(0);

    const phoneNumberVerification = async (phone) => {
        const verify = new RegExp("^(\\+98|0)?9\\d{9}$");

        if (!phone) {
            phoneVerificationError.value = 1;
            registerFormData.phoneErr = "شماره همراه را وارد کنید";
            return;
        }

        if (!verify.test(phone)) {
            phoneVerificationError.value = 1;
            registerFormData.phoneErr = "شماره همراه وارد شده معتبر نیست";
            return;
        }

        registerFormData.phoneErr = "";
        phoneVerificationError.value = "";

        try {
            const { data } = await adminApi.verifyPhoneNumberApi(phone);
            phoneCheckResponse.value = data;

            if (phoneCheckResponse.value === 1) {
                registerFormData.phoneErr = "شماره همراه قبلا ثبت شده است";
                phoneVerificationError.value = 1;
            } else {
                registerFormData.phoneErr = "";
                phoneVerificationError.value = 0;
            }
        } catch (error) {
            handleError("خطا در بررسی شماره همراه");
        }
    };

    const checkCodeMeli = async (nationalcode) => {
        if (nationalcode.length < 10) {
            registerFormData.nationalcodeErr = "شماره ملی را وارد کنید";
            nationalcodeValidationErr.value = 1;
            return;
        }
        registerFormData.nationalcodeErr = "";
        nationalcodeValidationErr.value = "";

        try {
            const { data } = await adminApi.verifyNationalCodeApi(nationalcode);
            nationalcodeResponse.value = data;
            if (nationalcodeResponse.value === 0) {
                registerFormData.nationalcodeErr = "کد ملی وارد شده معتبر نیست";
                nationalcodeValidationErr.value = 1;
            } else {
                nationalcodeValidationErr.value = 0;
            }
        } catch (error) {
            registerFormData.nationalcodeErr = "خطا در بررسی کد ملی";
        }
    };

    const fetchAllUsers = async () => {
        usersLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.allUsers();
            users.value = data;
        } catch (err) {
            handleError("در دریافت اطلاعات کاربران مشکلی پیش آمده است!");
        } finally {
            usersLoading.value = false;
        }
    };

    const changeUserActivation = async (userID, isActive) => {
        changeStatusLoading.value = true;
        try {
            await adminApi.csrfCookies();
            await adminApi.changeUserActivationApi(userID, isActive ? 0 : 1);
            const user = users.value.find((user) => user.id === userID);
            if (user) {
                user.is_active = isActive ? 0 : 1;
            }
        } catch (err) {
            handleError("در تغییر وضعیت کاربر مشکلی پیش آمده است");
        } finally {
            changeStatusLoading.value = false;
        }
    };
    const tagValidate = async () => {
        const tagVerify = new RegExp("\\b(\\d{3})([عﻉ])(\\d{2})\\b");
        if (!registerFormData.tag) {
            tagValidationErr.value = 1;
            registerFormData.tagErr = "پلاک را وارد کنید";
            return;
        }
        if (!tagVerify.test(registerFormData.tag)) {
            tagValidationErr.value = 1;
            registerFormData.tagErr = "پلاک وارد شده معتبر نیست";
            return;
        }
        tagValidationErr.value = 0;
        registerFormData.tagErr = "";
        // try {
        //   const { data } = await adminApi.verifyPlateApi(registerFormData.tag);

        //   if (data === 1) {
        //     registerFormData.tagErr = "این پلاک قبلا ثبت شده است";
        //     tagValidationErr.value = 1;
        //   } else {
        //     tagValidationErr.value = 0;
        //     registerFormData.tagErr = "";
        //   }
        // } catch (error) {
        //   handleError("خطا در بررسی شماره پلاک");
        // }
    };
    const validateRegisterFormData = (phone, nationalcode) => {
        phoneNumberVerification(phone);
        checkCodeMeli(nationalcode);
        tagValidate();
        let isValid = true;
        const fields = ["name", "family", "tag_serial_num"];

        fields.forEach((field) => {
            if (!registerFormData[field]) {
                registerFormData[`${field}Err`] = `این قسمت را پر کنید`;
                isValid = false;
            } else {
                registerFormData[`${field}Err`] = "";
                return isValid;
            }
        });

        if (
            isValid &&
            !nationalcodeValidationErr.value &&
            !phoneVerificationError.value &&
            !tagValidationErr.value
        ) {
            usersLoading.value = true;
            sendRegisterFormData();
        }
    };

    const sendRegisterFormData = async () => {
        usersLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const result = await Swal.fire({
                title: "از ثبت اطلاعات اطمینان دارید؟",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "ارسال",
                cancelButtonText: "انصراف",
            });

            if (result.isConfirmed) {
                const { data } = await adminApi.createTruck(
                    registerFormData.name,
                    registerFormData.family,
                    registerFormData.phone,
                    registerFormData.nationalcode,
                    registerFormData.smartcode,
                    registerFormData.tag,
                    registerFormData.tag_serial_num,
                    registerFormData.tankertype,
                    registerFormData.factory,
                    registerFormData.chassiscode,
                    registerFormData.enginecode,
                    registerFormData.vincode,
                    registerFormData.year
                );
                if (data.status) {
                    handleSuccess(data.message);
                    router.push({ path: "/admin/users" });
                } else {
                    handleError(data.message);
                    console.log(data.message);
                }
            }
        } catch (err) {
            handleError("در ثبت کاربر مشکلی پیش آمده است!");
            throw err;
        } finally {
            usersLoading.value = false;
            Object.keys(registerFormData).forEach((key) => {
                if (typeof registerFormData[key] !== "function") {
                    registerFormData[key] = "";
                }
            });
        }
    };

    return {
        users,
        fetchAllUsers,
        usersLoading,
        registerFormData,
        validateRegisterFormData,
        phoneNumberVerification,
        phoneVerificationError,
        checkCodeMeli,
        nationalcodeValidationErr,
        nationalcodeResponse,
        changeUserActivation,
        changeStatusLoading,
        tagValidationErr,
        tagValidate,
    };
});
// User update store
export const useUserUpdateStore = defineStore("updateStore", () => {
    const userData = ref([]);
    const router = useRouter();
    const updateUserDataLoading = ref(false);
    const phoneCheckResponse = ref("");
    const phoneVerificationError = ref(0);
    const nationalcodeResponse = ref("");
    const nationalcodeValidationErr = ref(0);
    const tagValidationErr = ref();
    const deleteCarLoading = ref(false);

    const updateUserData = reactive({
        name: "",
        nameErr: "",
        family: "",
        familyErr: "",
        phone: "",
        phoneErr: "",
        nationalcode: "",
        nationalcodeErr: "",
    });
    const addCarToDriverData = reactive({
        smartcode: "",
        tag: "",
        tagErr: "",
        tag_serial_num: "",
        tag_serial_numErr: "",
        tankertype: "",
        factory: "",
        chassiscode: "",
        enginecode: "",
        vincode: "",
        year: "",
    });

    const checkCodeMeli = async (nationalcode) => {
        if (nationalcode.length < 10) {
            updateUserData.nationalcodeErr = "شماره ملی را وارد کنید";
            nationalcodeValidationErr.value = 1;
            return;
        }
        updateUserData.nationalcodeErr = "";
        nationalcodeValidationErr.value = 0;

        try {
            const { data } = await adminApi.verifyNationalCodeApi(nationalcode);
            nationalcodeResponse.value = data;
            if (nationalcodeResponse.value === 0) {
                updateUserData.nationalcodeErr = "کد ملی وارد شده معتبر نیست";
                nationalcodeValidationErr.value = 1;
            } else {
                nationalcodeValidationErr.value = 0;
            }
        } catch (error) {
            updateUserData.nationalcodeErr = "خطا در بررسی کد ملی";
        }
    };

    const sendUserUpdateFormData = async (userID) => {
        updateUserDataLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const result = await Swal.fire({
                title: "از ویرایش اطلاعات اطمینان دارید؟",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "ارسال",
                cancelButtonText: "انصراف",
            });

            if (result.isConfirmed) {
                const response = await adminApi.updateUserApi(
                    userID,
                    updateUserData.name,
                    updateUserData.family,
                    updateUserData.nationalcode,
                    updateUserData.phone
                );
                console.log(response);

                if (response.data.status) {
                    handleSuccess(response.data.message);
                    router.push({ path: "/admin/users" });
                } else {
                    handleError(response.data.message);
                }
            }
        } catch (err) {
            handleError("در ویرایش اطلاعات مشکلی پیش آمده است!");
            throw err;
        } finally {
            updateUserDataLoading.value = false;
        }
    };

    const validateUserEditFormData = (userID, phone, nationalcode) => {
        phoneNumberVerification(phone);
        checkCodeMeli(nationalcode);
        let isValid = true;
        const fields = ["name", "family", "nationalcode", "phone"];

        fields.forEach((field) => {
            if (!updateUserData[field]) {
                updateUserData[`${field}Err`] = "این قسمت را پر کنید";
                isValid = false;
            } else {
                updateUserData[`${field}Err`] = "";
            }
        });

        if (
            isValid &&
            nationalcodeValidationErr.value === 0 &&
            phoneVerificationError.value === 0
        ) {
            updateUserDataLoading.value = true;
            sendUserUpdateFormData(userID);
        }
    };

    const editUserData = async (userID) => {
        updateUserDataLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.editUserApi(userID);
            userData.value = data[0];
            Object.assign(updateUserData, userData.value);
        } catch (err) {
            handleError("دریافت اطلاعات با مشکل مواجه شد");
        } finally {
            updateUserDataLoading.value = false;
        }
    };

    const phoneNumberVerification = async (phone) => {
        const verify = new RegExp("^(\\+98|0)?9\\d{9}$");
        if (!phone) {
            setPhoneError("شماره همراه را وارد کنید");
            return;
        }
        if (!verify.test(phone)) {
            setPhoneError("شماره همراه وارد شده معتبر نیست");
            return;
        }

        clearPhoneError();

        try {
            const { data } = await adminApi.verifyPhoneNumberApi(phone);
            phoneCheckResponse.value = data;

            if (
                phoneCheckResponse.value === 1 &&
                phone !== userData.value.phone
            ) {
                setPhoneError("شماره همراه قبلا ثبت شده است");
            } else {
                clearPhoneError();
            }
        } catch (error) {
            handleError("خطا در بررسی شماره همراه");
        }
    };

    const setPhoneError = (message) => {
        phoneVerificationError.value = 1;
        updateUserData.phoneErr = message;
    };

    const clearPhoneError = () => {
        updateUserData.phoneErr = "";
        phoneVerificationError.value = 0;
    };
    const tagValidate = async () => {
        const tagVerify = new RegExp("\\b(\\d{3})([عﻉ])(\\d{2})\\b");
        if (!addCarToDriverData.tag) {
            tagValidationErr.value = 1;
            addCarToDriverData.tagErr = "پلاک را وارد کنید";
            return;
        }
        if (!tagVerify.test(addCarToDriverData.tag)) {
            tagValidationErr.value = 1;
            addCarToDriverData.tagErr = "پلاک وارد شده معتبر نیست";
            return;
        }
        addCarToDriverData.tagErr = "";
        // try {
        //   const { data } = await adminApi.verifyPlateApi(addCarToDriverData.tag);
        //   if (data === 1) {
        //     addCarToDriverData.tagErr = "این پلاک قبلا ثبت شده است";
        //     tagValidationErr.value = 1;
        //   } else {
        //     tagValidationErr.value = 0;
        //     addCarToDriverData.tagErr = "";
        //   }
        // } catch (error) {
        //   handleError("خطا در بررسی شماره پلاک");
        // }
    };

    const addCarToDriverFormData = async (userID) => {
        updateUserDataLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const result = await Swal.fire({
                title: "از اضافه کردن نفتکش اطمینان دارید؟",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "ارسال",
                cancelButtonText: "انصراف",
            });

            if (result.isConfirmed) {
                const response = await adminApi.addCarToDriverApi(
                    userID,
                    addCarToDriverData.smartcode,
                    addCarToDriverData.tag,
                    addCarToDriverData.tag_serial_num,
                    addCarToDriverData.tankertype,
                    addCarToDriverData.factory,
                    addCarToDriverData.chassiscode,
                    addCarToDriverData.enginecode,
                    addCarToDriverData.vincode,
                    addCarToDriverData.year
                );
                if (response.data.status) {
                    handleSuccess(response.data.message);
                    router.go(0);
                } else {
                    handleError(response.data.message);
                }
            }
        } catch (err) {
            handleError("در اضافه کردن نفتکش مشکلی پیش آمده است!");
            throw err;
        } finally {
            updateUserDataLoading.value = false;
        }
    };
    const validateAddCarToUserFormData = (userID) => {
        tagValidate();
        let isValid = true;
        if (!addCarToDriverData.tag_serial_num) {
            addCarToDriverData.tag_serial_numErr =
                "شماره سریال پلاک را وارد کنید";
            isValid = false;
        } else {
            addCarToDriverData.tag_serial_numErr = "";
        }

        if (isValid && !tagValidationErr.value) {
            updateUserDataLoading.value = true;
            addCarToDriverFormData(userID);
        }
    };

    const deleteCarFromUser = async (carId) => {
        deleteCarLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const result = await Swal.fire({
                title: "از حذف نفتکش اطمینان دارید؟",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "ارسال",
                cancelButtonText: "انصراف",
            });
            if (result.isConfirmed) {
                const { data } = await adminApi.deleteCarFromUserApi(carId);
                console.log(data);
                if (data.status) {
                    handleSuccess(data.message);
                    router.go(0);
                } else {
                    handleError(data.message);
                }
            }
        } catch (err) {
            handleError("در حذف کردن نفتکش مشکلی پیش آمده است!");
            throw err;
        } finally {
            deleteCarLoading.value = false;
        }
    };
    return {
        updateUserDataLoading,
        editUserData,
        updateUserData,
        userData,
        phoneNumberVerification,
        nationalcodeValidationErr,
        validateUserEditFormData,
        sendUserUpdateFormData,
        checkCodeMeli,
        phoneVerificationError,
        addCarToDriverFormData,
        validateAddCarToUserFormData,
        addCarToDriverData,
        tagValidate,
        deleteCarFromUser,
        tagValidationErr,
        deleteCarLoading,
    };
});

// Request store
export const useRequestStore = defineStore("requestStore", () => {
    const notificationStore = useNotificationStore();
    const requests = ref([]);
    const requestLoading = ref(false);
    const error = ref(null);
    const userRequest = ref([]);
    const isRequestAnswerValid = ref(false);
    const updateRequestsLoading = ref(false);
    const requestAnswer = reactive({
        answer: "",
        answerErr: "",
        status: "",
        statusErr: "",
    });

    const fetchAllRequests = async () => {
        requestLoading.value = true;
        error.value = null;
        try {
            const { data } = await adminApi.allRequests();
            requests.value = data;
        } catch (err) {
            handleError("در دریافت درخواست ها مشکلی پیش آمده است!");
            error.value = err;
        } finally {
            requestLoading.value = false;
        }
    };
    const editRequests = async (reqID) => {
        requestLoading.value = true;
        error.value = null;
        try {
            const { data } = await adminApi.editRequestsApi(reqID);
            userRequest.value = data;
            requestAnswer.answer = data.answer;
            requestAnswer.status = data.status;
        } catch (err) {
            handleError("در دریافت درخواست مشکلی پیش آمده است!");
            error.value = err;
        } finally {
            requestLoading.value = false;
        }
    };

    const updateRequests = async (reqID) => {
        validateUpdateRequestIfEmpty();
        if (updateRequestsLoading.value || isRequestAnswerValid.value) {
            updateRequestsLoading.value = true;
            try {
                await adminApi.csrfCookies();
                const result = await Swal.fire({
                    title: "از ارسال جواب اطمینان دارید؟",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "ارسال",
                    cancelButtonText: "انصراف",
                });

                if (result.isConfirmed) {
                    const { data } = await adminApi.updateRequestsApi(
                        reqID,
                        requestAnswer.answer,
                        requestAnswer.status
                    );

                    if (data.status) {
                        handleSuccess(data.message);
                        router.push("/admin/requests");
                        if (notificationStore.requestCount.unAnswerReqs > 0) {
                            notificationStore.requestCount.unAnswerReqs--;
                        }
                    } else {
                        handleError(data.message);
                    }
                }
            } catch (err) {
                handleError("ارسال پاسخ با مشکل مواجه شده است");
                throw err;
            } finally {
                updateRequestsLoading.value = false;
                isRequestAnswerValid.value = false;
            }
        }
    };

    function validateUpdateRequestIfEmpty() {
        let isValid = true;

        if (!requestAnswer.answer) {
            requestAnswer.answerErr = `قسمت پاسخ را پر کنید`;
            isValid = false;
        } else {
            requestAnswer.answerErr = "";
        }

        if (requestAnswer.status == 0) {
            requestAnswer.statusErr = "وضعیت باید انتخاب شود";
            isValid = false;
        } else {
            requestAnswer.statusErr = "";
        }

        if (isValid) {
            updateRequestsLoading.value = true;
            isRequestAnswerValid.value = true;
        }
    }
    return {
        requests,
        fetchAllRequests,
        requestLoading,
        userRequest,
        editRequests,
        requestAnswer,
        updateRequestsLoading,
        updateRequests,
        validateUpdateRequestIfEmpty,
        error,
        isRequestAnswerValid,
    };
});

// Bills store
export const useBillStore = defineStore("billStore", () => {
    const yearBills = ref([]);
    const selectedYear = ref("");
    const monthBills = ref([]);
    const allBills = ref([]);
    const fetchBillLoading = ref(false);
    const downloadBillLoading = ref(false);
    const sameTagLoader = ref(false);
    const uploadBillLoading = ref(false);
    const synchronizationBillsTag = ref("");
    const synchronizationSummaryTag = ref("");
    const billsDirectories = ref([]);
    const sameTag = ref([]);

    const fetchBillsYears = async () => {
        fetchBillLoading.value = true;
        try {
            const { data } = await adminApi.getBillYearApi();
            yearBills.value = data;
        } catch (err) {
            handleError("در دریافت صورتحساب ها مشکلی پیش آمده است!");
        } finally {
            fetchBillLoading.value = false;
        }
    };
    const fetchBillsMonths = async (year) => {
        fetchBillLoading.value = true;
        selectedYear.value = year;
        try {
            const { data } = await adminApi.getBillMonthApi(year);
            monthBills.value = data;
        } catch (err) {
            handleError("در دریافت صورتحساب ها مشکلی پیش آمده است!");
        } finally {
            fetchBillLoading.value = false;
        }
    };
    const fetchAllBills = async (month) => {
        fetchBillLoading.value = true;
        try {
            const { data } = await adminApi.getAllBillApi(
                selectedYear.value,
                month
            );
            allBills.value = data;
        } catch (err) {
            handleError("در دریافت صورتحساب ها مشکلی پیش آمده است!");
        } finally {
            fetchBillLoading.value = false;
        }
    };
    const uploadPdf = async (formData, month) => {
        try {
            const { data: folderExists } = await adminApi.checkBillFolder(
                month
            );
            const proceed = folderExists.status
                ? await Swal.fire({
                    title: "فایل این ماه قبلا بارگزاری شده است، دوباره ارسال می‌کنید؟",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "ارسال مجدد",
                    cancelButtonText: "انصراف",
                }).then((r) => r.isConfirmed)
                : await Swal.fire({
                    title: "آیا از ارسال فایل اطمینان دارید؟",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "بله",
                    cancelButtonText: "خیر",
                }).then((r) => r.isConfirmed);

            if (!proceed) return;

            uploadBillLoading.value = true;
            await adminApi.csrfCookies();
            const { data } = await adminApi.uploadPdfApi(formData);

            if (data) {
                await adminApi.runPythonApi(data.filePath, data.month);

                let timerInterval;
                Swal.fire({
                    title: data.message,
                    html: "در حال پردازش فایل...<br><b></b>",
                    icon: "question",
                    timer: 120000,
                    timerProgressBar: true,
                    didOpen: () => {
                        const b = Swal.getHtmlContainer().querySelector("b");
                        timerInterval = setInterval(() => {
                            const remaining = Swal.getTimerLeft();
                            if (b && remaining !== undefined) {
                                const seconds = Math.ceil(remaining / 1000);
                                b.textContent = `${seconds} ثانیه باقی مانده`;
                            }
                        }, 1000);
                    },
                    willClose: () => {
                        clearInterval(timerInterval);
                    },
                });
            } else {
                handleError("در تفکیک فایل‌ها خطایی پیش آمده است");
            }
        } catch (err) {
            console.error(err);
            handleError("خطا در بارگزاری صورت حساب");
        } finally {
            uploadBillLoading.value = false;
        }
    };

    const synchronizationBills = async (month) => {
        uploadBillLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.synchronizationBillsApi(month);
            synchronizationBillsTag.value = data;

            handleSuccess(data.message);
        } catch (err) {
            handleError("خطا در همگام سازی");
        } finally {
            uploadBillLoading.value = false;
        }
    };
    const uploadZip = async (formData) => {
        uploadBillLoading.value = true;
        const result = await Swal.fire({
            title: "از ثبت اطلاعات اطمینان دارید؟",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "ارسال",
            cancelButtonText: "انصراف",
        });

        if (result.isConfirmed) {
            try {
                await adminApi.csrfCookies();
                const { data } = await adminApi.uploadZipApi(formData);
                if (data.status) {
                    handleSuccess(data.message);
                } else {
                    handleError(data.message);
                }
            } catch (err) {
                handleError("خطا در بارگزاری صورت حساب");
            } finally {
                uploadBillLoading.value = false;
            }
        }
    };
    const synchronizationSummary = async (month) => {
        console.log(month);

        uploadBillLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.synchronizationSummaryApi(month);
            synchronizationSummaryTag.value = data;
            handleSuccess(data.message);
        } catch (err) {
            handleError("خطا در همگام سازی");
        } finally {
            uploadBillLoading.value = false;
        }
    };
    const getBillsDirectories = async () => {
        fetchBillLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.billsDirectoriesApi();
            console.log(data);

            if (data.status) {
                billsDirectories.value = data.directories;
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("خطا در دریافت اطلاعات");
            console.log(err);
        } finally {
            fetchBillLoading.value = false;
        }
    };
    const downloadAllBill = async (year, month) => {
        downloadBillLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.downloadAllBillApi(year, month);

            if (data) {
                const link = document.createElement("a");
                link.href = data.download_url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else handleError("فایل موجود نیست");
        } catch (err) {
            handleError("خطا در دریافت اطلاعات");
            console.log(err);
        } finally {
            downloadBillLoading.value = false;
        }
    };
    const downloadAllExcels = async (year, month) => {
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.downloadAllBillExcelsRequestApi(
                year,
                month
            );
            console.log(data);
            
            if (data.status && data.download_url) {
                downloadBillLoading.value = true;
                handleSuccess(data.message);
                const link = document.createElement("a");
                link.href = data.download_url;
                link.setAttribute("download", `all-bills-${year}-${month}.zip`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                downloadBillLoading.value = false;
            } else if (data.status && !data.download_url) {
                downloadBillLoading.value = true;
                handleSuccess(data.message);
                setTimeout(async () => {
                    try {
                        const { data } =
                            await adminApi.downloadAllBillExcelsApi(
                                year,
                                month
                            );
                        if (data.status) {
                            console.log(data);
                            handleSuccess(data.message);
                            const link = document.createElement("a");
                            link.href = data.download_url;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } else handleError(data.message);
                    } catch (err) {
                        handleError("خطا در دریافت فایل نهایی");
                        console.log(err);
                    }
                    downloadBillLoading.value = false;
                }, 1000 * 60 * 8);
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("خطا در دریافت اطلاعات");
            console.log(err);
        }
    };
    const findSameTag = async (month) => {
        sameTagLoader.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.findSameTagApi(month);
            console.log(data);

            if (!data.status) {
                handleSuccess(data.message);
            } else {
                sameTag.value = data;
            }
        } catch (err) {
            console.log(err);
            handleError("خطا در دریافت اطلاعات");
        } finally {
            sameTagLoader.value = false;
        }
    };
    return {
        yearBills,
        monthBills,
        allBills,
        fetchBillLoading,
        fetchAllBills,
        fetchBillsMonths,
        fetchBillsYears,
        selectedYear,
        uploadBillLoading,
        uploadPdf,
        synchronizationBills,
        synchronizationBillsTag,
        synchronizationSummaryTag,
        synchronizationSummary,
        uploadZip,
        billsDirectories,
        getBillsDirectories,
        sameTag,
        findSameTag,
        sameTagLoader,
        downloadBillLoading,
        downloadAllBill,
        downloadAllExcels,
    };
});

export const useAdjustmentStore = defineStore("adjustmentStore", () => {
    const yearAdjustments = ref([]);
    const selectedYear = ref("");
    const monthAdjustments = ref([]);
    const allAdjustments = ref([]);
    const fetchAdjustmentLoading = ref(false);
    const downloadAdjLoading = ref(false);
    const uploadAdjustmentLoading = ref(false);
    const synchronizationAdjustmentTag = ref("");
    const synchronizationSummaryTag = ref("");
    const adjustmentDirectories = ref([]);
    const sameTag = ref([]);
    const sameTagLoader = ref(false);

    const findSameTag = async (month) => {
        sameTagLoader.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.findAdjSameTagApi(month);
            console.log(data);

            if (!data.status) {
                handleSuccess(data.message);
            } else {
                sameTag.value = data;
            }
        } catch (err) {
            console.log(err);
            handleError("خطا در دریافت اطلاعات");
        } finally {
            sameTagLoader.value = false;
        }
    };
    const fetchAdjustmentsYears = async () => {
        fetchAdjustmentLoading.value = true;
        try {
            const { data } = await adminApi.getAdjustmentYearApi();
            yearAdjustments.value = data;
        } catch (err) {
            handleError("در دریافت صورتحساب ها مشکلی پیش آمده است!");
        } finally {
            fetchAdjustmentLoading.value = false;
        }
    };
    const fetchAdjustmentsMonths = async (year) => {
        fetchAdjustmentLoading.value = true;
        selectedYear.value = year;
        try {
            const { data } = await adminApi.getAdjustmentMonthApi(year);
            monthAdjustments.value = data;
        } catch (err) {
            handleError("در دریافت صورتحساب ها مشکلی پیش آمده است!");
        } finally {
            fetchAdjustmentLoading.value = false;
        }
    };
    const fetchAllAdjustments = async (month) => {
        fetchAdjustmentLoading.value = true;
        try {
            const { data } = await adminApi.getAllAdjustmentApi(
                selectedYear.value,
                month
            );
            allAdjustments.value = data;
        } catch (err) {
            handleError("در دریافت صورتحساب ها مشکلی پیش آمده است!");
        } finally {
            fetchAdjustmentLoading.value = false;
        }
    };
    const uploadAdjustmentPdf = async (formData, month) => {
        uploadAdjustmentLoading.value = true;

        try {
            const { data: folderExists } =
                await adminApi.checkAdjustmentsFolder(month);

            const proceed = folderExists.status
                ? await Swal.fire({
                    title: "فایل این ماه قبلا بارگزاری شده است، دوباره ارسال می‌کنید؟",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "ارسال مجدد",
                    cancelButtonText: "انصراف",
                }).then((r) => r.isConfirmed)
                : true;

            if (!proceed) return;

            await adminApi.csrfCookies();
            const { data } = await adminApi.uploadAdjustmentPdfApi(formData);

            if (data.status) {
                await adminApi.runAdjustmentPythonApi(
                    data.filePath,
                    data.month
                );

                let timerInterval;
                Swal.fire({
                    title: data.message,
                    html: "در حال پردازش فایل تعدیلی...<br><b></b>",
                    icon: "question",
                    timer: 120000,
                    timerProgressBar: true,
                    didOpen: () => {
                        const b = Swal.getHtmlContainer().querySelector("b");
                        timerInterval = setInterval(() => {
                            const remaining = Swal.getTimerLeft();
                            if (b && remaining !== undefined) {
                                const seconds = Math.ceil(remaining / 1000);
                                b.textContent = `${seconds} ثانیه باقی مانده`;
                            }
                        }, 1000);
                    },
                    willClose: () => {
                        clearInterval(timerInterval);
                    },
                });
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("خطا در بارگزاری صورت حساب تعدیلی");
            console.error(err);
        } finally {
            uploadAdjustmentLoading.value = false;
        }
    };

    const synchronizationAdjustment = async (month) => {
        uploadAdjustmentLoading.value = true;
        console.log(month);
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.synchronizationAdjustmentApi(month);
            synchronizationAdjustmentTag.value = data;
            if (data.status) {
                handleSuccess(data.message);
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("خطا در همگام سازی");
        } finally {
            uploadAdjustmentLoading.value = false;
        }
    };
    const uploadAdjustmentZip = async (formData) => {
        uploadAdjustmentLoading.value = true;
        const result = await Swal.fire({
            title: "از ثبت اطلاعات اطمینان دارید؟",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "ارسال",
            cancelButtonText: "انصراف",
        });

        if (result.isConfirmed) {
            try {
                await adminApi.csrfCookies();
                const { data } = await adminApi.uploadAdjustmentZipApi(
                    formData
                );

                if (data.status) {
                    handleSuccess(data.message);
                } else {
                    handleError(data.message);
                }
            } catch (err) {
                handleError("خطا در بارگزاری صورت حساب");
            } finally {
                uploadAdjustmentLoading.value = false;
            }
        }
    };
    const synchronizationAdjustmentSummary = async (month) => {
        uploadAdjustmentLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.synchronizationAdjustmentSummaryApi(
                month
            );
            synchronizationSummaryTag.value = data;
            console.log(data);
            if (data.status) {
                handleSuccess(data.message);
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("خطا در همگام سازی");
        } finally {
            uploadAdjustmentLoading.value = false;
        }
    };
    const getAdjustmentDirectories = async () => {
        fetchAdjustmentLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.adjustmentDirectoriesApi();
            if (data.status) {

                adjustmentDirectories.value = data.directories;
            } else {
                handleError(data.message);
            }
            console.log(adjustmentDirectories.value);
        } catch (err) {
            handleError("خطا در دریافت اطلاعات");
            console.log(err);
        } finally {
            fetchAdjustmentLoading.value = false;
        }
    };
    const downloadAllAdjustment = async (year, month) => {
        downloadAdjLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.downloadAllAdjApi(year, month);
            if (data) {
                const link = document.createElement("a");
                link.href = data.download_url;
                // link.setAttribute("download", "all-bills.zip");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else handleError("فایل موجود نیست");
        } catch (err) {
            handleError("خطا در دریافت اطلاعات");
            console.log(err);
        } finally {
            downloadAdjLoading.value = false;
        }
    };
    const downloadAllExcels = async (year, month) => {
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.downloadAllAdjExcelsRequestApi(
                year,
                month
            );
            if (data.status && data.download_url) {
                downloadAdjLoading.value = true;
                handleSuccess(data.message);
                const link = document.createElement("a");
                link.href = data.download_url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                downloadAdjLoading.value = false;
            } else if (data.status && !data.download_url) {
                downloadAdjLoading.value = true;
                handleSuccess(data.message);
                setTimeout(async () => {
                    try {
                        const { data } = await adminApi.downloadAllAdjExcelsApi(
                            year,
                            month
                        );
                        if (data.status) {
                            console.log(data);
                            handleSuccess(data.message);
                            const link = document.createElement("a");
                            link.href = data.download_url;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } else handleError(data.message);
                    } catch (err) {
                        handleError("خطا در دریافت فایل نهایی");
                        console.log(err);
                    }
                }, 1000 * 60 * 8);
                downloadAdjLoading.value = false;
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("خطا در دریافت اطلاعات");
            console.log(err);
        }
    };
    return {
        yearAdjustments,
        selectedYear,
        monthAdjustments,
        allAdjustments,
        fetchAdjustmentLoading,
        fetchAdjustmentsYears,
        fetchAdjustmentsMonths,
        fetchAllAdjustments,
        synchronizationAdjustment,
        synchronizationAdjustmentTag,
        getAdjustmentDirectories,
        adjustmentDirectories,
        uploadAdjustmentPdf,
        uploadAdjustmentZip,
        synchronizationAdjustmentSummary,
        synchronizationSummaryTag,
        uploadAdjustmentLoading,
        sameTag,
        findSameTag,
        sameTagLoader,
        downloadAdjLoading,
        downloadAllAdjustment,
        downloadAllExcels,
    };
});

export const useSettingStore = defineStore("settingStore", () => {
    const settingLoading = ref(false);

    const companyPhoneVerifyErr = ref(false);
    const companyLogoName = ref("");
    const basicSiteInfos = reactive({
        receiverNumber: "",
        currentYear: "",
        companyName: "",
        companyPhone1: "",
        companyPhone2: "",
        companyPhone3: "",
        companyAddress: "",
        aboutUs: "",
    });
    const infosError = reactive({
        mobileErr: "",
        phoneErr: "",
        currentYearErr: "",
    });
    const phoneVerificationError = ref("");
    const currentYearIsEmptyError = ref("");

    const getCompanyInfo = async () => {
        settingLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.getCompanyInfoApi();
            companyLogoName.value = data.companyLogo;

            data.forEach((item) => {
                if (
                    Object.prototype.hasOwnProperty.call(
                        basicSiteInfos,
                        item.key
                    )
                ) {
                    basicSiteInfos[item.key] = item.value;
                }
            });
            localStorage.setItem("companyName", basicSiteInfos.companyName);
        } catch (err) {
            handleError("دریافت اطلاعات با مشکل مواجه شد");
        } finally {
            settingLoading.value = false;
        }
    };
    const uploadLogo = async (formData) => {
        settingLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.uploadLogoApi(formData);
            if (data.status) {
                handleSuccess(data.message);
            } else {
                handleError(data.message);
            }
        } catch (error) {
            handleError("خطا در بارگزاری نشان");
        } finally {
            settingLoading.value = false;
        }
    };

    const setCompanyInfo = async () => {
        settingLoading.value = true;
        try {
            await adminApi.csrfCookies();

            const { data } = await adminApi.updateCompanyInfo(basicSiteInfos);
            if (data.status) {
                handleSuccess(data.message);
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("بروزرسانی اطلاعات با مشکل مواجه شد");
        } finally {
            settingLoading.value = false;
        }
    };
    const phoneNumberVerification = async () => {
        const verify = new RegExp("^(\\+98|0)?9\\d{9}$");

        // بررسی شماره همراه
        if (!basicSiteInfos.receiverNumber) {
            phoneVerificationError.value = 1;
            infosError.mobileErr = "شماره همراه را وارد کنید";
            return;
        }

        if (!verify.test(basicSiteInfos.receiverNumber)) {
            phoneVerificationError.value = 1;
            infosError.mobileErr = "شماره همراه وارد شده معتبر نیست";
            return;
        }

        if (!basicSiteInfos.currentYear) {
            currentYearIsEmptyError.value = 1;
            infosError.currentYearErr = "سال جاری را وارد کنید";
            return;
        }

        const phoneErrors = [1, 2, 3];
        if (phoneErrors.includes(companyPhoneVerifyErr.value)) {
            return;
        }

        infosError.mobileErr = "";
        infosError.currentYearErr = "";
        phoneVerificationError.value = 0;
        currentYearIsEmptyError.value = 0;

        setCompanyInfo();
    };

    const companyPhoneVerification = async () => {
        const verify = new RegExp("^0[0-9]{2,}[0-9]{7,}$");
        const phoneFields = [
            { value: basicSiteInfos.companyPhone1, errorIndex: 1 },
            { value: basicSiteInfos.companyPhone2, errorIndex: 2 },
            { value: basicSiteInfos.companyPhone3, errorIndex: 3 },
        ];

        for (const field of phoneFields) {
            if (field.value && !verify.test(field.value)) {
                infosError.phoneErr = "شماره ثابت وارد شده معتبر نیست";
                companyPhoneVerifyErr.value = field.errorIndex;
                return;
            }
        }

        infosError.phoneErr = "";
        companyPhoneVerifyErr.value = 0;
    };

    return {
        basicSiteInfos,
        uploadLogo,
        settingLoading,
        setCompanyInfo,
        getCompanyInfo,
        phoneNumberVerification,
        infosError,
        companyPhoneVerification,
        companyPhoneVerifyErr,
        phoneVerificationError,
        currentYearIsEmptyError,
        companyLogoName,
    };
});

export const useNotificationStore = defineStore("notificationStore", () => {
    const requestCount = ref("");

    const getUnansweredRequests = async () => {
        await adminApi.csrfCookies();
        const response = await adminApi.getUnansweredRequestsApi();
        requestCount.value = response.data;
    };

    return {
        getUnansweredRequests,
        requestCount,
    };
});

export const useAdminSettingsStore = defineStore("adminSettingsStore", () => {
    const adminSettingPageLoading = ref(false);
    const adminSettingLoading = ref(false);
    const registeredAdminInfo = ref([]);
    const editAdminData = ref([]);
    const phoneVerificationError = ref(0);
    const nationalcodeValidationErr = ref(0);
    const registerAdminFormData = reactive({
        name: "",
        nameErr: "",
        family: "",
        familyErr: "",
        phone: "",
        phoneErr: "",
        nationalcode: "",
        nationalcodeErr: "",
    });
    const updateAdminData = reactive({
        name: "",
        nameErr: "",
        family: "",
        familyErr: "",
        phone: "",
        phoneErr: "",
        nationalcode: "",
        nationalcodeErr: "",
    });

    const phoneNumberVerification = async (adminData) => {
        const verify = new RegExp("^(\\+98|0)?9\\d{9}$");

        if (!adminData.phone) {
            adminData.phoneErr = "شماره همراه را وارد کنید";
            phoneVerificationError.value = 1;

            return false;
        }

        if (!verify.test(adminData.phone)) {
            adminData.phoneErr = "شماره همراه وارد شده معتبر نیست";
            phoneVerificationError.value = 1;

            return false;
        }
        adminData.phoneErr = "";
        phoneVerificationError.value = 0;

        try {
            const { data } = await adminApi.verifyPhoneNumberApi(
                adminData.phone
            );
            if (data === 1 && editAdminData?.value?.phone !== adminData.phone) {
                adminData.phoneErr = "شماره همراه قبلا ثبت شده است";
                phoneVerificationError.value = 1;
            } else {
                adminData.phoneErr = "";
                phoneVerificationError.value = 0;
            }
        } catch (error) {
            handleError("خطا در بررسی شماره همراه");
        }
        return true;
    };

    const checkCodeMeli = async (adminData) => {
        if (adminData.nationalcode.length < 10) {
            adminData.nationalcodeErr = "شماره ملی را وارد کنید";
            nationalcodeValidationErr.value = 1;
            return false;
        }

        adminData.nationalcodeErr = "";
        nationalcodeValidationErr.value = 0;

        try {
            const { data } = await adminApi.verifyNationalCodeApi(
                adminData.nationalcode
            );
            if (data === 0) {
                adminData.nationalcodeErr = "کد ملی وارد شده معتبر نیست";
                nationalcodeValidationErr.value = 1;

                return false;
            }
            nationalcodeValidationErr.value = 0;
            return true;
        } catch (error) {
            adminData.nationalcodeErr = "خطا در بررسی کد ملی";
            return false;
        }
    };

    const validateRegisterAdminFormData = (adminData) => {
        phoneNumberVerification(adminData);
        checkCodeMeli(adminData);
        let isValid = true;
        const fields = ["name", "family"];

        fields.forEach((field) => {
            if (!adminData[field]) {
                adminData[`${field}Err`] = `این قسمت را پر کنید`;
                isValid = false;
            } else {
                adminData[`${field}Err`] = "";
                return isValid;
            }
        });

        if (
            isValid &&
            !nationalcodeValidationErr.value &&
            !phoneVerificationError.value
        ) {
            adminSettingLoading.value = true;
            sendRegisterAdminFormData();
        }
    };
    const validateUpdateAdminFormData = (adminData, userID) => {
        phoneNumberVerification(adminData);
        checkCodeMeli(adminData);

        let isValid = true;
        const fields = ["name", "family"];

        fields.forEach((field) => {
            if (!adminData[field]) {
                adminData[`${field}Err`] = `این قسمت را پر کنید`;
                isValid = false;
            } else {
                adminData[`${field}Err`] = "";
                return isValid;
            }
        });

        if (
            isValid &&
            !nationalcodeValidationErr.value &&
            !phoneVerificationError.value
        ) {
            adminSettingLoading.value = true;
            sendUpdateAdminFormData(userID);
        }
    };
    const sendRegisterAdminFormData = async () => {
        adminSettingLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const result = await Swal.fire({
                title: "از ثبت اطلاعات اطمینان دارید؟",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "ارسال",
                cancelButtonText: "انصراف",
            });

            if (result.isConfirmed) {
                const { data } = await adminApi.storeAdminInfoApi(
                    registerAdminFormData.name,
                    registerAdminFormData.family,
                    registerAdminFormData.phone,
                    registerAdminFormData.nationalcode
                );

                if (data.status) {
                    handleSuccess(data.message);

                    getAdminInfo();
                    Object.keys(registerAdminFormData).forEach((key) => {
                        if (typeof registerAdminFormData[key] !== "function") {
                            registerAdminFormData[key] = "";
                        }
                    });
                } else {
                    handleError(data.message);
                }
            }
        } catch (err) {
            handleError("در ثبت مدیر مشکلی پیش آمده است!");
            throw err;
        } finally {
            adminSettingLoading.value = false;
        }
    };
    const sendUpdateAdminFormData = async (userID) => {
        adminSettingLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const result = await Swal.fire({
                title: "از ثبت اطلاعات اطمینان دارید؟",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "ارسال",
                cancelButtonText: "انصراف",
            });

            if (result.isConfirmed) {
                const { data } = await adminApi.updateAdminApi(
                    userID,
                    updateAdminData.name,
                    updateAdminData.family,
                    updateAdminData.phone,
                    updateAdminData.nationalcode
                );

                if (data.status) {
                    handleSuccess(data.message);
                    router.push({ path: "/admin/adminsetting" });
                } else {
                    handleError(data.message);
                }
            }
        } catch (err) {
            handleError("در ثبت مدیر مشکلی پیش آمده است!");
            throw err;
        } finally {
            adminSettingLoading.value = false;
        }
    };
    const getAdminInfo = async () => {
        adminSettingPageLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.getAdminInfoApi();
            registeredAdminInfo.value = data;
        } catch (err) {
            handleError("دریافت اطلاعات با مشکل مواجه شد");
        } finally {
            adminSettingPageLoading.value = false;
        }
    };
    const editAdminInfo = async (userID) => {
        adminSettingPageLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.editAdminInfoApi(userID);
            editAdminData.value = data;
            Object.assign(updateAdminData, data);
        } catch (err) {
            handleError("دریافت اطلاعات با مشکل مواجه شد");
        } finally {
            adminSettingPageLoading.value = false;
        }
    };

    const updateAdminInfo = async (userID) => {
        adminSettingLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.updateAdminApi(
                userID,
                updateAdminData.name,
                updateAdminData.family,
                updateAdminData.phone,
                updateAdminData.nationalcode
            );
            if (data.status) {
                handleSuccess(data.message);
                Object.keys(registerAdminFormData).forEach((key) => {
                    if (typeof registerAdminFormData[key] !== "function") {
                        registerAdminFormData[key] = "";
                    }
                });
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("در ویرایش اطلاعات مشکلی پیش آمده است");
        } finally {
            adminSettingLoading.value = false;
        }
    };

    const changeUserActivation = async (userID, isActive) => {
        adminSettingLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.changeAdminActivationApi(
                userID,
                isActive ? 0 : 1
            );
            if (data.success) {
                const admin = registeredAdminInfo.value.find(
                    (user) => user.id === userID
                );
                if (admin) {
                    admin.is_active = isActive ? 0 : 1;
                }
            }
        } catch (err) {
            handleError("در تغییر وضعیت کاربر مشکلی پیش آمده است");
        } finally {
            adminSettingLoading.value = false;
        }
    };
    const deleteAdmin = async (userID) => {
        adminSettingLoading.value = true;

        try {
            await adminApi.csrfCookies();
            const result = await Swal.fire({
                title: "از ثبت اطلاعات اطمینان دارید؟",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "ارسال",
                cancelButtonText: "انصراف",
            });

            if (result.isConfirmed) {
                const { data } = await adminApi.deleteAdminApi(userID);
                if (data.status) {
                    handleSuccess(data.message);
                    getAdminInfo();
                } else {
                    handleError(data.message);
                }
            }
        } catch (err) {
            handleError("در حذف مدیر مشکلی پیش آمده است");
        } finally {
            adminSettingLoading.value = false;
        }
    };

    return {
        adminSettingPageLoading,
        adminSettingLoading,
        registeredAdminInfo,
        getAdminInfo,
        registerAdminFormData,
        phoneNumberVerification,
        checkCodeMeli,
        validateUpdateAdminFormData,
        validateRegisterAdminFormData,
        sendRegisterAdminFormData,
        sendUpdateAdminFormData,
        phoneVerificationError,
        nationalcodeValidationErr,
        deleteAdmin,
        editAdminInfo,
        updateAdminData,
        updateAdminInfo,
        editAdminData,
        changeUserActivation,
    };
});

export const usePublicNoticeStore = defineStore("publicNoticeStore", () => {
    const noticeLoading = ref(false);
    const changeStatusLoading = ref(false);
    const allNoticeData = ref([]);
    const activeNoticeData = ref([]);
    const editNoticeData = reactive({
        title: "",
        text: "",
    });
    const createNoticeData = reactive({
        title: "",
        text: "",
    });
    const getAllNotice = async () => {
        noticeLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.getAllNoticeApi();
            allNoticeData.value = data;
        } catch (err) {
            handleError(err);
        } finally {
            noticeLoading.value = false;
        }
    };
    const getActiveNotice = async () => {
        noticeLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.getActiveNoticeApi();
            activeNoticeData.value = data;
        } catch (err) {
            handleError(err);
        } finally {
            noticeLoading.value = false;
        }
    };
    const createNotice = async () => {
        const result = await Swal.fire({
            text: "از ارسال اطلاعیه اطمینان دارید؟",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "ارسال",
            cancelButtonText: "انصراف",
        });
        noticeLoading.value = true;
        if (result.isConfirmed) {
            try {
                await adminApi.csrfCookies();
                const { data } = await adminApi.createNoticeApi(
                    createNoticeData.title,
                    createNoticeData.text
                );
                if (data.status) {
                    handleSuccess(data.message);
                    router.push({ path: "/admin/notice" });
                } else {
                    handleError(data.message);
                }
            } catch (err) {
                handleError(err);
            } finally {
                noticeLoading.value = false;
                createNoticeData.title = "";
                createNoticeData.text = "";
            }
        }
    };
    const editNotice = async (noticeID) => {
        noticeLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.editNoticeApi(noticeID);
            editNoticeData.title = data.title;
            editNoticeData.text = data.text;
        } catch (err) {
            handleError("در دریافت اطلاعات مشکلی پیش آمده است");
        } finally {
            noticeLoading.value = false;
        }
    };
    const updateNotice = async (noticeID) => {
        noticeLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.updateNoticeApi(
                noticeID,
                editNoticeData.title,
                editNoticeData.text
            );
            if (data.status) {
                handleSuccess(data.message);
                router.push({ path: "/admin/notice" });
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("بروزرسانی اطلاعیه با مشکل مواجه شد");
        } finally {
            noticeLoading.value = false;
        }
    };

    const changeNoticeActivation = async (noticeID, isActive) => {
        changeStatusLoading.value = true;
        try {
            await adminApi.csrfCookies();
            await adminApi.changeNoticeActivationApi(
                noticeID,
                isActive ? 0 : 1
            );
            const noticeData = allNoticeData.value.find(
                (notice) => notice.id === noticeID
            );
            if (noticeData) {
                noticeData.is_active = isActive ? 0 : 1;
            }
        } catch (err) {
            handleError("در تغییر وضعیت کاربر مشکلی پیش آمده است");
        } finally {
            changeStatusLoading.value = false;
        }
    };
    const deleteNotice = async (noticeID) => {
        try {
            await adminApi.csrfCookies();
            const result = await Swal.fire({
                title: "از حذف اطلاعیه اطمینان دارید؟",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "حذف",
                cancelButtonText: "انصراف",
            });

            if (result.isConfirmed) {
                noticeLoading.value = true;

                const { data } = await adminApi.deleteNoticeApi(noticeID);
                if (data.status) {
                    handleSuccess(data.message);
                    getAllNotice();
                } else {
                    handleError(data.message);
                }
            }
        } catch (err) {
            handleError("در حذف اطلاعیه مشکلی پیش آمده است");
        } finally {
            noticeLoading.value = false;
        }
    };
    return {
        noticeLoading,
        changeStatusLoading,
        createNoticeData,
        createNotice,
        allNoticeData,
        getAllNotice,
        activeNoticeData,
        getActiveNotice,
        changeNoticeActivation,
        deleteNotice,
        editNotice,
        updateNotice,
        editNoticeData,
    };
});

export const useImportExcelStore = defineStore("importExcelStore", () => {
    const importLoading = ref(false);

    const uploadUsersExcel = async (formData) => {
        importLoading.value = true;

        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.uploadUsersExcelApi(formData);
            console.log(data);
            if (data.status) {
                handleSuccess(data.message);
            } else {
                handleError(data.message);
            }
        } catch (err) {
            handleError("خطا در بارگزاری فایل");
            console.log(err);
        } finally {
            importLoading.value = false;
        }
    };

    return {
        uploadUsersExcel,
        importLoading,
    };
});
export const useDriverStore = defineStore("driverStore", () => {
    const driverListLoading = ref(null);
    const allDrivers = ref([]);

    const fetchAllDriver = async () => {
        driverListLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.allDriverApi();
            allDrivers.value = data;
            console.log(data);
        } catch (err) {
            handleError("در دریافت اطلاعات رانندگان مشکلی پیش آمده است!");
        } finally {
            driverListLoading.value = false;
        }
    };

    return {
        driverListLoading,
        allDrivers,
        fetchAllDriver,
    };
});
export const useShippingStore = defineStore("shippingStore", () => {
    const allShippedBills = ref([]);
    const dataGetLoading = ref(false);
    const sendBillLoading = ref(false);
    const driverName = ref("");
    const validationErrors = reactive({
        nationalCode: "",
        tankerTag: "",
    });

    const fieldLabels = {
        BHNumber: "شماره بارنامه",
        senderCode: "کد فرستنده",
        destinationCode: "کد مقصد",
        destinationCode2: "کد مقصد دوم",
        naftkore: "نفتکوره",
        sendType: "نوع ارسال",
        tankerTag: "پلاک تانکر",
        tankerTagSerial: "سریال تانکر",
        contractorCode: "کد پیمانکار",
        unknown1: "فیلد ۱ ناشناخته",
        temperature: "دما",
        normalAmount: "حجم نرمال",
        value60: "حجم معادل ۶۰ درجه",
        destinationAddress: "آدرس مقصد",
        unknown2: "فیلد ۲ ناشناخته",
        RNumber: "شماره رسید",
        plumbDate: "تاریخ پلمپ",
    };
    const billData = reactive({
        BHNumber: "",
        senderCode: "",
        destinationCode: "",
        destinationCode2: "",
        naftkore: "",
        sendType: "",
        tankerTag: "",
        tankerTagSerial: "",
        contractorCode: "",
        unknown1: "",
        temperature: "",
        normalAmount: "",
        value60: "",
        destinationAddress: "",
        unknown2: "",
        RNumber: "",
        plumbDate: "",
    });
    const filters = reactive({
        tankerTag: "",
        bhNumber: "",
        nationalCode: "",
        fromDate: "",
        toDate: "",
    });

    const requiredFields = [
        "BHNumber",
        "senderCode",
        "destinationCode",
        "normalAmount",
        "value60",
        "plumbDate",
        "naftkore",
        "tankerTag",
        "tankerTagSerial",
    ];
    const validateNationalCode = (code) => {
        if (code.length !== 10 || !/^\d{10}$/.test(code)) {
            validationErrors.nationalCode = "کد ملی باید ۱۰ رقم باشد.";
            return false;
        }
        validationErrors.nationalCode = "";
        return true;
    };

    const validateTankerTag = (tag) => {
        const tagPattern = new RegExp("\\b(\\d{3})([عﻉ])(\\d{2})\\b");
        if (!tagPattern.test(tag)) {
            validationErrors.tankerTag = "فرمت پلاک معتبر نیست";
            return false;
        }
        validationErrors.tankerTag = "";
        return true;
    };
    const resetBillData = () => {
        Object.assign(billData, {
            BHNumber: "",
            senderCode: "",
            destinationCode: "",
            destinationCode2: "",
            naftkore: "",
            sendType: "",
            tankerTag: "",
            tankerTagSerial: "",
            contractorCode: "",
            unknown1: "",
            temperature: "",
            normalAmount: "",
            value60: "",
            destinationAddress: "",
            unknown2: "",
            RNumber: "",
            plumbDate: "",
        });
    };

    const parseCSV = (csvString) => {
        return csvString.split(";");
    };

    const sendBillData = async () => {
        sendBillLoading.value = true;
        try {
            await adminApi.csrfCookies();
            const { data } = await adminApi.sendShippingBillApi(
                billData.tankerTag,
                billData.tankerTagSerial
            );
            console.log(billData.tankerTag, billData.tankerTagSerial);

            if (data.status) {
                driverName.value = data.nameFamily;
            } else {
                handleError(data.message);
            }
        } catch {
            handleError("خطایی رخ داده است!");
        } finally {
            sendBillLoading.value = false;
        }
    };
    const storeShippingBill = async () => {
        if (!validateTankerTag(billData.tankerTag)) {
            return;
        }
        try {
            const result = await Swal.fire({
                title: `از ثبت اطلاعات برای ${driverName.value} اطمینان دارید؟`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "ارسال",
                cancelButtonText: "انصراف",
            });

            if (result.isConfirmed) {
                sendBillLoading.value = true;

                const { data } = await adminApi.storeShippingBillApi(billData);
                console.log(data);
                if (data.status === true) {
                    handleSuccess("بارنامه با موفقیت ثبت شد");
                    Object.keys(billData).forEach((key) => {
                        billData[key] = "";
                    });
                } else {
                    handleError(
                        "در ثبت اطلاعات بارنامه ها مشکلی پیش آمده است!"
                    );
                }
            }
        } catch (err) {
            handleError("در ثبت اطلاعات بارنامه ها مشکلی پیش آمده است!");
            console.log(err);
        } finally {
            sendBillLoading.value = false;
            resetBillData();
        }
    };
    const filterShippingBills = async () => {
        dataGetLoading.value = true;
        if (filters.nationalCode && !validateNationalCode(filters.nationalCode))
            return;
        if (filters.tankerTag && !validateTankerTag(filters.tankerTag)) return;
        const activeFilters = Object.fromEntries(
            Object.entries(filters).filter(([, value]) => value.trim() !== "")
        );

        ["fromDate", "toDate"].forEach((field) => {
            if (activeFilters[field]) {
                const formatted = moment(
                    activeFilters[field],
                    "jYYYY/jMM/jDD"
                ).format("YYYY-MM-DD");
                activeFilters[field] = formatted;
            }
        });

        try {
            if (Object.keys(activeFilters).length === 0) {
                const { data } = await adminApi.filterShippingBillsApi();
                if (data.status) {
                    allShippedBills.value = data;
                } else {
                    handleError(data.message);
                }
                return;
            }

            await adminApi.csrfCookies();
            const { data } = await adminApi.filterShippingBillsApi(
                activeFilters
            );
            if (data.status) {
                allShippedBills.value = data;
            } else {
                handleError(data.message);
            }
        } catch (error) {
            handleError("در دریافت اطلاعات بارنامه ها مشکلی پیش آمده است!");
        } finally {
            dataGetLoading.value = false;
        }
    };

    return {
        allShippedBills,
        dataGetLoading,
        sendBillLoading,
        sendBillData,
        billData,
        parseCSV,
        fieldLabels,
        filters,
        driverName,
        filterShippingBills,
        storeShippingBill,
        requiredFields,
        validationErrors,
        validateNationalCode,
        validateTankerTag,
    };
});

export const useBackupStore = defineStore("backupStore", () => {
    const backupDataLoading = ref(false);
    const selectedUserDataType = ref("");
    const selectionErr = reactive({ users: "", files: "", db: "" });
    const selectedFileType = ref("");
    const selectedDbType = ref("");

    const backupUserData = async () => {
        if (!selectedUserDataType.value) {
            selectionErr.users = "یکی از گزینه ها را انتخاب کنید";
        } else {
            selectionErr.users = "";
            backupDataLoading.value = true;
            try {
                await adminApi.csrfCookies();
                const { data } = await adminApi.backupUserDataApi(
                    selectedUserDataType.value
                );
                console.log(data);

                if (data) {
                    const link = document.createElement("a");
                    link.href = data.download_url;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else handleError("فایل موجود نیست");
            } catch (err) {
                handleError("خطا در دریافت اطلاعات");
                console.log(err);
            } finally {
                backupDataLoading.value = false;
            }
        }
    };
    const backupFiles = async () => {
        if (!selectedFileType.value) {
            selectionErr.files = "یکی از گزینه ها را انتخاب کنید";
        } else {
            selectionErr.files = "";
            backupDataLoading.value = true;
            try {
                await adminApi.csrfCookies();
                const { data } = await adminApi.backupFilesApi(
                    selectedFileType.value
                );
                console.log(data);

                // if (data) {
                //     const link = document.createElement("a");
                //     link.href = data.download_url;
                //     document.body.appendChild(link);
                //     link.click();
                //     document.body.removeChild(link);
                // } else handleError("فایل موجود نیست");
            } catch (err) {
                handleError("خطا در دریافت اطلاعات");
                console.log(err);
            } finally {
                backupDataLoading.value = false;
            }
        }
    };
    const backupDb = async () => {
        if (!selectedDbType.value) {
            selectionErr.db = "نوع فایل دیتابیس را انتخاب کنید";
        } else {
            selectionErr.db = "";
            backupDataLoading.value = true;
            try {
                await adminApi.csrfCookies();
                const { data } = await adminApi.backupDataBaseApi(
                    selectedDbType.value
                );
                console.log(data);

                if (data) {
                    const link = document.createElement("a");
                    link.href = data.download_url;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else handleError(data.message);
            } catch (err) {
                handleError("خطا در دریافت اطلاعات");
                console.log(err);
            } finally {
                backupDataLoading.value = false;
            }
        }
    };
    return {
        backupDataLoading,
        backupUserData,
        selectedUserDataType,
        selectionErr,
        selectedFileType,
        backupFiles,
        selectedDbType,
        backupDb,
    };
});
